/**
 * One target/desired waypoint for the arm in a motion plan.
 *
 * Motion plans are generated from a list of these.
 */
import * as zod from 'zod';

import { CartesianPose } from '@sb/geometry';

import { ArmJointPositions } from './ArmJointPositions';
import { GripperOpenness } from './GripperOpenness';
import { TCPOffsetOption } from './TCPOffsetOption';

export const MotionKind = zod.enum([
  'joint',
  'line',
  'line_inc',
  'rotation',
  'arc',
  'weave',
]);
export type MotionKind = zod.infer<typeof MotionKind>;

export const PoseArmTarget = zod.object({
  pose: CartesianPose,
  tcpOption: TCPOffsetOption.optional(),
  gripperID: zod.string().optional(),
});

export type PoseArmTarget = zod.infer<typeof PoseArmTarget>;

export const JointArmTarget = zod.object({
  jointAngles: ArmJointPositions,
});

export type JointArmTarget = zod.infer<typeof JointArmTarget>;

export const CalibratedJointArmTarget = zod.object({
  calibrationPose: CartesianPose,
  jointAngles: ArmJointPositions,
});

export type CalibratedJointArmTarget = zod.infer<
  typeof CalibratedJointArmTarget
>;

export const GraspCandidate = zod.object({
  gripperOpenness: GripperOpenness,
  preGraspPose: CartesianPose,
  graspPose: CartesianPose,
  postGraspPose: CartesianPose,
});
// This is used for the grasp API.
// It is currently not recommended that users be allowed
// to use this in step configurations directly, even where
// ArmTarget is specified in the schema.
export const GraspArmTarget = zod.object({
  gripperEvent: GraspCandidate,
});

export type GraspArmTarget = zod.infer<typeof GraspArmTarget>;

const EndTarget = zod.union([
  CalibratedJointArmTarget,
  PoseArmTarget,
  JointArmTarget,
  GraspArmTarget,
]);

export const ArmTarget = zod
  .object({
    motionKind: MotionKind,
    blendRadius: zod.number().optional(),
    // ArmTargets from waypoints have an effective tcpOption that should be used for motion planning
    // ArmTargets from other sources will instead set the TCPOffsetOption field directly via `routineContext.setTCPOffsetOption`
    // Ideally all ArmTargets would have an effectiveTcpOption field, but that's a larger change to make.
    effectiveTCPOption: TCPOffsetOption.optional(),
    gripperID: zod.string().optional(),
    // For arc targets: when true, the start orientation is maintained throughout the arc
    maintainStartOrientation: zod.boolean().optional(),
    useDeterministicPlanner: zod.boolean().optional(),
  })
  .and(EndTarget);

export type ArmTarget = zod.infer<typeof ArmTarget>;
